import { DEFAULT_PHONE_NUMBER, isNone, Maybe } from '@crocodille/util';
import { useEffect, useRef } from 'react';

import { Customer } from '../modules/auth.v2';
import { CustomerAddress } from '../modules/customer-addresses';
import {
  EMAIL_VALIDATOR_V2,
  getFieldValues,
  REQUIRED_VALIDATOR_V2,
  useForm,
  useFormField,
  useValidator,
  v2_useMaxLengthValidator,
  v2_useMinLengthValidator,
} from '../modules/forms';
import { PHONE_NUMBER_VALIDATOR } from '../modules/phone';

/**
 * Flat structure of form data.
 */
export interface v2_OrderCompleteFormData {
  readonly email: string;
  readonly paymentMethod: Maybe<string>;
  readonly useRememberedCard: boolean;
  readonly expectedPickupTime: Maybe<string>;
  readonly kitchenNote: string;
  readonly courierNote: string;
  readonly shippingAddressFirstName: string;
  readonly shippingAddressLastName: string;
  readonly shippingAddressPhoneNumber: string;
  readonly useDifferentBillingAddress: boolean;
  readonly billingAddressFirstName: string;
  readonly billingAddressLastName: string;
  readonly billingAddressCompany: string;
  readonly billingAddressCRN: string;
  readonly billingAddressVAT: string;
  readonly billingAddressStreet: string;
  readonly billingAddressNumber: string;
  readonly billingAddressCity: string;
  readonly billingAddressPostcode: string;
}

export interface UseOrderCompleteFormOptions {
  /**
   * Handles the form submit (after validation).
   */
  onSubmit: (data: v2_OrderCompleteFormData) => void;
  /**
   * Payment method data for the form.
   */
  readonly paymentMethods: readonly { value: string }[];
  /**
   * Pickup times data for the form.
   */
  readonly pickupTimes: readonly { value: string }[];
  /**
   * Delivery times data for the form.
   */
  readonly scheduledDeliveryTimes: readonly { value: string }[];
  /**
   * Info about current customer.
   */
  readonly customer?: Maybe<Customer>;
  /**
   * Address to fill in case billing address is used.
   */
  readonly billingAddress?: Maybe<CustomerAddress>;
  /**
   * Pre-selected payment method.
   */
  readonly defaultPaymentMethod?: Maybe<string>;
}

export function v2_useOrderCompleteForm({
  onSubmit,
  paymentMethods,
  defaultPaymentMethod = null,
  pickupTimes,
  scheduledDeliveryTimes,
  customer,
  billingAddress,
}: UseOrderCompleteFormOptions) {
  pickupTimes = pickupTimes.length === 0 ? scheduledDeliveryTimes : pickupTimes;

  const email = useFormField({
    value: customer?.email ?? '',
    required: true,
    validators: [REQUIRED_VALIDATOR_V2, EMAIL_VALIDATOR_V2],
  });

  useEffect(() => {
    email.reset(customer?.email);
  }, [customer?.email]);

  const paymentMethod = useFormField({
    value: defaultPaymentMethod,
    required: true,
    validators: [REQUIRED_VALIDATOR_V2],
  });

  //
  // Select the default payment method, when no payment method is selected.
  //
  useEffect(() => {
    if (
      defaultPaymentMethod &&
      (isNone(paymentMethod.value) ||
        (paymentMethods.length > 0 &&
          paymentMethods.every(({ value }) => value !== paymentMethod.value)))
    ) {
      paymentMethod.reset(defaultPaymentMethod);
    }
  }, [
    paymentMethod.value,
    defaultPaymentMethod,
    paymentMethods.map(({ value }) => value).join(),
  ]);

  const useRememberedCard = useFormField({
    value: customer?.rememberCard ?? false,
  });

  useEffect(() => {
    useRememberedCard.reset(customer?.rememberCard ?? false);
  }, [customer?.rememberCard]);

  const expectedPickupTime = useFormField({
    value: pickupTimes[0]?.value,
    required: true,
  });

  //
  // Select the first available pickup time, when no pickup time is selected
  // or the selected pickup time is not in the array of pickup times given.
  //
  useEffect(() => {
    if (
      isNone(expectedPickupTime.value) ||
      (pickupTimes.length > 0 &&
        pickupTimes.every(({ value }) => value !== expectedPickupTime.value))
    ) {
      expectedPickupTime.reset(pickupTimes[0]?.value);
    }
  }, [expectedPickupTime.value, pickupTimes.map(({ value }) => value).join()]);

  const kitchenNote = useFormField({ value: '' });
  const courierNote = useFormField({ value: '' });

  const nameMinLengthValidator = v2_useMinLengthValidator(2);
  const nameMaxLengthValidator = v2_useMaxLengthValidator(30);

  const shippingAddressFirstName = useFormField({
    value: customer?.firstName ?? '',
    required: true,
    validators: [
      REQUIRED_VALIDATOR_V2,
      nameMinLengthValidator,
      nameMaxLengthValidator,
    ],
  });

  useEffect(() => {
    shippingAddressFirstName.reset(customer?.firstName ?? '');
  }, [customer?.firstName]);

  const shippingAddressLastName = useFormField({
    value: customer?.lastName ?? '',
    required: true,
    validators: [
      REQUIRED_VALIDATOR_V2,
      nameMinLengthValidator,
      nameMaxLengthValidator,
    ],
  });

  useEffect(() => {
    shippingAddressLastName.reset(customer?.lastName ?? '');
  }, [customer?.lastName]);

  const shippingAddressPhoneNumber = useFormField({
    value: customer?.phoneNumber ?? DEFAULT_PHONE_NUMBER,
    required: true,
    validators: [REQUIRED_VALIDATOR_V2, PHONE_NUMBER_VALIDATOR],
  });

  useEffect(() => {
    if (customer?.phoneNumber) {
      shippingAddressPhoneNumber.reset(customer.phoneNumber);
    }
  }, [customer?.phoneNumber]);

  const useDifferentBillingAddress = useFormField({ value: false });

  const requiredIfDifferentBillingAddress = useValidator(
    (value: unknown) => {
      if (useDifferentBillingAddress.value) {
        return REQUIRED_VALIDATOR_V2(value);
      } else {
        return null;
      }
    },
    [useDifferentBillingAddress.value],
  );

  const billingAddressFirstName = useFormField({
    value: '',
    required: true,
    validators: [
      requiredIfDifferentBillingAddress,
      nameMinLengthValidator,
      nameMaxLengthValidator,
    ],
  });
  const billingAddressLastName = useFormField({
    value: '',
    required: true,
    validators: [
      requiredIfDifferentBillingAddress,
      nameMinLengthValidator,
      nameMaxLengthValidator,
    ],
  });

  const billingAddressCompany = useFormField({
    value: '',
  });

  const crnMaxLengthValidator = v2_useMaxLengthValidator(8);

  const billingAddressCRN = useFormField({
    value: '',
    validators: [crnMaxLengthValidator],
  });

  const vatMaxLengthValidator = v2_useMaxLengthValidator(14);

  const billingAddressVAT = useFormField({
    value: '',
    validators: [vatMaxLengthValidator],
  });
  const billingAddressStreet = useFormField({
    value: '',
    required: true,
    validators: [requiredIfDifferentBillingAddress],
  });
  const billingAddressNumber = useFormField({
    value: '',
    required: true,
    validators: [requiredIfDifferentBillingAddress],
  });
  const billingAddressCity = useFormField({
    value: '',
    required: true,
    validators: [requiredIfDifferentBillingAddress],
  });
  const billingAddressPostcode = useFormField({
    value: '',
    required: true,
    validators: [requiredIfDifferentBillingAddress],
  });

  const form = useForm({
    fields: {
      email,
      paymentMethod,
      useRememberedCard,
      expectedPickupTime,
      kitchenNote,
      courierNote,
      shippingAddressFirstName,
      shippingAddressLastName,
      shippingAddressPhoneNumber,
      useDifferentBillingAddress,
      billingAddressFirstName,
      billingAddressLastName,
      billingAddressCompany,
      billingAddressCRN,
      billingAddressVAT,
      billingAddressStreet,
      billingAddressNumber,
      billingAddressCity,
      billingAddressPostcode,
    },
    onSubmit,
  });

  const fieldsRef = useRef(form.fields);
  fieldsRef.current = form.fields;

  useEffect(() => {
    if (!useDifferentBillingAddress.value) {
      return;
    }

    const toFill = billingAddress;

    if (!toFill) {
      return;
    }

    form.reset({
      ...getFieldValues(fieldsRef.current),
      billingAddressCity: toFill.city ?? '',
      billingAddressCompany: toFill.company ?? '',
      billingAddressFirstName: toFill.firstName ?? '',
      billingAddressLastName: toFill.lastName ?? '',
      billingAddressPostcode: toFill.postcode ?? '',
      billingAddressStreet: toFill.street ?? '',
      billingAddressNumber: toFill.number ?? '',
      billingAddressCRN: toFill.crn ?? '',
      billingAddressVAT: toFill.vat ?? '',
    });
  }, [form.reset, useDifferentBillingAddress.value, billingAddress]);

  return form;
}
